import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useLocation, Navigate } from "react-router-dom";
import { mutate } from "swr";
import { IconArrowLeft, IconPencil } from "@tabler/icons-react";
import { useProductRequestDetails } from "../../hooks/useProductRequestDetails";
import { useAdminProductRequestDetails } from "../../hooks/useAdminProductRequestDetails";
import { useMe } from "../../../../swr";
import { Role } from "../../../../api";
import { Heading, Text } from "../../../../components/common/typography";
import { Button } from "../../../../components/common/button";
import { Switch } from "../../../../components/common/switch/switch";
import { SectionCard } from "../../../../components/common/section-card";
import { Badge } from "../../../../components/common/badge";
import { ShimmerLoader } from "../../../../components/common/shimmer";
import { AppRoutes } from "../../../../common/routes/app_routes";
import { ProductRequestImage } from "../components/product-request-image";
import { WarrantyCard, type Warranty } from "../../../products/presentation/components/warranty-card";
import { BasicInfoSection } from "../../../products/presentation/components/basic-info-section";
import {
  VariantsBuilderSection,
  type ResolvedVariant,
} from "../../../products/presentation/components/variants-builder-section";
import type {
  VariantOption,
  VariantOptionAxis,
} from "../../../products/presentation/components/option-card";
import type { VariantRowEdit } from "../../../products/presentation/components/variant-pricing-row";
import {
  AXIS_DTO_FIELD,
  VARIANT_AXES,
  type NonEmptyVariantAxis,
} from "../../../products/presentation/components/variant-axis-registry";
import { ProductRequestApprovalForm } from "../components/product-request-approval-form";
import { ProductRequestStatusMessages } from "../components/product-request-status-messages";
import { adminProductRequestsService } from "../../../api_client";
import { ProductRequestDtoStatusEnum, ReviewProductRequestDtoStatusEnum } from "../../../../api";
import type { ProductRequestDto, ReviewProductRequestDto } from "../../../../api";
import { showErrorToast, showSuccessToast } from "../../../../utils/toasts";

function getStatusBadge(request: ProductRequestDto) {
  if (request.isDraft) {
    return <Badge label="Draft" state="pending" />;
  }
  switch (request.status) {
    case ProductRequestDtoStatusEnum.Pending:
      return <Badge label="Pending" state="pending" />;
    case ProductRequestDtoStatusEnum.Approved:
      return <Badge label="Approved" state="success" />;
    case ProductRequestDtoStatusEnum.Rejected:
      return <Badge label="Rejected" state="error" />;
    default:
      return <Badge label={status} state="pending" />;
  }
}

/** Convert API image (base64 / URL / data URI) to a usable <img> src. */
function toImageSrc(image?: string | null): string | undefined {
  if (!image) return undefined;
  if (/^(https?:|data:|blob:|\/)/i.test(image)) return image;
  const prefix = image.substring(0, 12);
  let type = "png";
  if (prefix.startsWith("/9j/")) type = "jpeg";
  else if (prefix.startsWith("UklGR")) type = "webp";
  else if (prefix.startsWith("R0lGODlh")) type = "gif";
  return `data:image/${type};base64,${image}`;
}

/**
 * Map the request's persisted variant rows back into the new builder's
 * (options, variantEdits) shape, so the same UI renders read-only.
 *
 * Walks every registered axis (RAM, Storage, Color, CPU, Screen Size,
 * Resolution) and reads the corresponding DTO field — so adding a new axis
 * to the registry automatically surfaces it here.
 */
function buildBuilderState(request: ProductRequestDto): {
  options: VariantOption[];
  edits: Record<string, VariantRowEdit>;
} {
  const idsByAxis: Record<NonEmptyVariantAxis, Set<string>> = {
    "RAM": new Set(),
    "Storage": new Set(),
    "Color": new Set(),
    "CPU": new Set(),
  };

  type V = ProductRequestDto["variants"][number];

  function axisId(v: V, axis: NonEmptyVariantAxis): number | null | undefined {
    return (v as unknown as Record<string, number | null | undefined>)[
      AXIS_DTO_FIELD[axis] as string
    ];
  }

  for (const v of request.variants) {
    for (const axis of VARIANT_AXES) {
      const id = axisId(v, axis);
      if (id != null) idsByAxis[axis].add(String(id));
    }
  }

  const options: VariantOption[] = [];
  let nextId = 1;
  for (const axis of VARIANT_AXES) {
    if (idsByAxis[axis].size > 0) {
      options.push({ id: nextId++, name: axis, valueIds: Array.from(idsByAxis[axis]) });
    }
  }

  const edits: Record<string, VariantRowEdit> = {};
  for (const v of request.variants) {
    const parts: string[] = [];
    for (const axis of VARIANT_AXES) {
      const id = axisId(v, axis);
      if (id != null) parts.push(`${axis}:${id}`);
    }
    const key = parts.join("|");
    if (!key) continue;
    edits[key] = {
      sku: v.sku ?? "",
      stock: v.quantity != null ? String(v.quantity) : "",
      conditionId: v.conditionId != null ? String(v.conditionId) : "",
      model: v.model ?? "",
      imagePreviewUrl: toImageSrc(v.image),
      pricingTiers:
        (v.pricingTiers ?? []).length > 0
          ? [...v.pricingTiers]
              .sort((a, b) => a.minQty - b.minQty)
              .map((tier, index) => ({
                id: index + 1,
                minQty: String(tier.minQty),
                price: String(tier.price),
              }))
          : [{ id: 1, minQty: "1", price: "" }],
    };
  }

  return { options, edits };
}

export function ProductRequestDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useMe();
  const isAdmin = user?.role === Role.Admin;
  const [showApprovalForm, setShowApprovalForm] = useState(false);

  if (isAdmin && id) {
    return <Navigate to={AppRoutes.ADMIN_PRODUCT_REQUEST_REVIEW.replace(":id", id)} replace />;
  }

  // Only one of these actually fires — the other gets a null key and is
  // skipped. Calling both unconditionally caused admin views to 404 on the
  // user-scoped endpoint (which filters by userId) and seller views to 404
  // on the admin endpoint (which requires admin role).
  const supplierRequest = useProductRequestDetails(!isAdmin ? id : null);
  const adminRequest = useAdminProductRequestDetails(isAdmin ? id : null);
  const {
    data: request,
    isLoading,
    error,
    mutate: mutateRequest,
  } = isAdmin ? adminRequest : supplierRequest;

  useEffect(() => {
    if (isAdmin && request?.status === ProductRequestDtoStatusEnum.Pending) {
      setShowApprovalForm(true);
    }
  }, [isAdmin, request?.status]);

  // Derive builder state from the request — must be called unconditionally.
  const builder = useMemo(() => {
    if (!request) return { options: [] as VariantOption[], edits: {} as Record<string, VariantRowEdit> };
    return buildBuilderState(request);
  }, [request]);

  const variantsEnabled = builder.options.length > 0;

  // Resolve attribute-value labels surfaced by OptionCard so we can render
  // the variants table even though we only persist IDs in option.valueIds.
  const [labelsByAxis, setLabelsByAxis] = useState<Partial<Record<VariantOptionAxis, Record<string, string>>>>({});
  const handleLabelsResolved = (axis: VariantOptionAxis, labels: Record<string, string>) => {
    setLabelsByAxis((prev) => {
      const existing = prev[axis] ?? {};
      const sameSize = Object.keys(existing).length === Object.keys(labels).length;
      const sameEntries = sameSize && Object.entries(labels).every(([k, v]) => existing[k] === v);
      if (sameEntries) return prev;
      return { ...prev, [axis]: { ...existing, ...labels } };
    });
  };

  const variants: ResolvedVariant[] = useMemo(() => {
    if (!variantsEnabled || builder.options.length === 0) return [];
    type Cell = { axis: VariantOptionAxis; valueId: string; label: string };
    const matrix: Cell[][] = builder.options.map((opt) =>
      opt.valueIds.map((vid) => ({
        axis: opt.name,
        valueId: vid,
        label: labelsByAxis[opt.name]?.[vid] ?? vid,
      })),
    );
    const combos = matrix.reduce<Cell[][]>(
      (acc, row) => acc.flatMap((combo) => row.map((cell) => [...combo, cell])),
      [[]],
    );
    return combos
      .map((combo): ResolvedVariant => {
        const axisValues: Partial<Record<NonEmptyVariantAxis, number>> = {};
        for (const cell of combo) {
          if (!cell.axis) continue;
          const id = Number(cell.valueId);
          if (!Number.isNaN(id)) axisValues[cell.axis as NonEmptyVariantAxis] = id;
        }
        return {
          key: combo.map((c) => `${c.axis}:${c.valueId}`).join("|"),
          label: combo.map((c) => c.label).join(" / "),
          axisValues,
        };
      })
      // Only keep combos that actually exist on the request (others are
      // cartesian phantoms that the seller never submitted).
      .filter((v) => builder.edits[v.key] != null);
  }, [variantsEnabled, builder.options, builder.edits, labelsByAxis]);

  if (isLoading) {
    return (
      <div className="flex min-h-full w-full flex-col bg-bgLight">
        <div className="flex flex-1 flex-col gap-6 px-8 pt-8 pb-6 max-md:px-4 max-md:pt-6">
          <ShimmerLoader message="Loading product request details…" />
        </div>
      </div>
    );
  }

  if (error || !request) {
    return (
      <div className="flex min-h-full w-full flex-col bg-bgLight">
        <div className="flex flex-1 flex-col gap-6 px-8 pt-8 pb-6 max-md:px-4 max-md:pt-6">
          <SectionCard shadow title="Error" className="gap-2">
            <Text variant="body" className="text-accentError">
              Failed to load product request. Please try again later.
            </Text>
          </SectionCard>
        </div>
      </div>
    );
  }

  // warrantyProviderId is exposed by the backend response but won't appear on
  // the generated client until `npm run generate:api` runs after the BE
  // schema/DTO change. The cast disappears after regen.
  const r = request as typeof request & { warrantyProviderId?: number | null };
  const warranty: Warranty = {
    id: 1,
    warrantyId: request.warrantyId != null ? String(request.warrantyId) : "",
    warrantyTypeId: request.warrantyTypeId != null ? String(request.warrantyTypeId) : "",
    warrantyProviderId: r.warrantyProviderId != null ? String(r.warrantyProviderId) : "",
  };

  const location = useLocation();
  const fromBulkUpload = (location.state as { fromBulkUpload?: boolean } | null)?.fromBulkUpload;
  const backLabel = fromBulkUpload
    ? "Back to Import"
    : isAdmin ? "Back to Product Requests" : "Back to Products";
  const backRoute = fromBulkUpload
    ? AppRoutes.MY_PRODUCT_BULK_UPLOAD_RESULTS
    : isAdmin ? AppRoutes.ADMIN_PRODUCT_REQUESTS : AppRoutes.MY_PRODUCT_REQUESTS;

  const handleApprove = async (
    variantPricingTiers: ReviewProductRequestDto["variantPricingTiers"],
  ) => {
    try {
      const reviewDto: ReviewProductRequestDto = {
        status: ReviewProductRequestDtoStatusEnum.Approved,
        variantPricingTiers,
      };
      await adminProductRequestsService.adminReviewProductRequest(request.id, reviewDto);
      showSuccessToast("Product request approved successfully");
      setShowApprovalForm(false);
      mutate(
        (key) =>
          typeof key === "string" &&
          (key.startsWith("admin-product-requests") ||
            key.startsWith("product-requests-grouped-by-user") ||
            key.startsWith("admin-product-request-details") ||
            key.startsWith("product-request-details")),
      );
      mutateRequest();
      navigate(backRoute);
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (err as { message?: string })?.message ||
        "Failed to approve product request";
      showErrorToast(message);
    }
  };

  return (
    <div className="flex min-h-full w-full flex-col bg-bgLight">
      <div className="flex flex-1 flex-col gap-6 px-8 pt-8 pb-6 max-md:px-4 max-md:pt-6">
        <div className="flex items-center gap-2 max-md:flex-wrap">
          <Button
            variant="ghostAccent"
            size="sm"
            leftIcon={<IconArrowLeft size={18} />}
            onClick={() => navigate(backRoute)}
            className="!h-auto !px-0 !py-0"
          >
            <Heading variant="h3" tone="inherit">{backLabel}</Heading>
          </Button>
          {/* Phones: the request name gets its own line instead of a truncated sliver. */}
          <Text variant="bodySm" tone="muted" className="max-md:hidden">/</Text>
          <Heading variant="h3" tone="dark" className="truncate max-md:basis-full max-md:whitespace-normal">{request.name}</Heading>
          {getStatusBadge(request)}
          {!isAdmin && request.isDraft && (
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<IconPencil size={16} />}
              onClick={() =>
                navigate(AppRoutes.MY_PRODUCT_REQUEST_EDIT.replace(":id", request.id))
              }
            >
              Edit Draft
            </Button>
          )}
        </div>

        <SectionCard
          shadow
          title="Main Product Image"
          subtitle="This is the hero image shown at the top of the request."
          className="gap-5"
        >
          <ProductRequestImage
            image={request.image || request.variants[0]?.image || ""}
            productName={request.name}
          />
          {(request.galleryImages?.length ?? 0) > 0 && (
            <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
              {request.galleryImages!.map((src, i) => (
                <img
                  key={`${i}-${src.slice(0, 32)}`}
                  src={toImageSrc(src)}
                  alt={`${request.name} ${i + 1}`}
                  className="aspect-square w-full rounded-lg border border-borderDefault object-cover"
                />
              ))}
            </div>
          )}
        </SectionCard>

        <BasicInfoSection
          name={request.name}                        onNameChange={() => {}}
          brandId={request.brandId != null ? String(request.brandId) : ""}                 onBrandIdChange={() => {}}
          categoryId={request.categoryId != null ? String(request.categoryId) : ""}        onCategoryIdChange={() => {}}
          // Top-level SKU on the request matches the implicit single-variant
          // SKU when variants aren't used. Surface it for parity with the form.
          sku={request.variants[0]?.sku ?? ""}        onSkuChange={() => {}}
          countryOfOriginId={request.countryOfOriginId != null ? String(request.countryOfOriginId) : ""}
          onCountryOfOriginIdChange={() => {}}
          dealTypeId={request.dealTypeId != null ? String(request.dealTypeId) : ""}
          onDealTypeIdChange={() => {}}
          screenSizeId=""
          onScreenSizeIdChange={() => {}}
          resolutionId=""
          onResolutionIdChange={() => {}}
          notes={request.notes ?? ""}
          onNotesChange={() => {}}
          description={request.description ?? ""}    onDescriptionChange={() => {}}
          readOnly
        />

        <SectionCard shadow title="Warranty information" className="gap-5">
          <div className="flex flex-col gap-4">
            <WarrantyCard warranty={warranty} index={0} onRemove={() => {}} onUpdate={() => {}} readOnly />
          </div>
        </SectionCard>

        <VariantsBuilderSection
          enabled={variantsEnabled}
          onEnabledChange={() => {}}
          options={builder.options}
          onOptionRemove={() => {}}
          onOptionUpdate={() => {}}
          onOptionAdd={() => {}}
          onLabelsResolved={handleLabelsResolved}
          variants={variants}
          variantEdits={builder.edits}
          onVariantEditUpdate={() => {}}
          onVariantRemove={() => {}}
          activeOptionsCount={builder.options.length}
          readOnly
        />

        {isAdmin && request.status === ProductRequestDtoStatusEnum.Approved ? (
          <ProductRequestApprovalForm
            request={request}
            onCancel={() => {}}
            readOnly
            title="Approved pricing"
            subtitle="These are the Procurtal pricing tiers that were saved when the request was approved."
          />
        ) : null}

        <SectionCard shadow title="Visibility" className="gap-5">
          <div className="flex items-center gap-3">
            <Switch checked disabled aria-label="List product in catalog" onChange={() => {}} />
            <Text variant="body" tone="dark">List product in catalog immediately</Text>
          </div>
        </SectionCard>

        <ProductRequestStatusMessages
          rejectionReason={request.rejectionReason}
          odooProductId={request.odooProductTemplateId}
        />

        {isAdmin &&
          request.status === ProductRequestDtoStatusEnum.Pending &&
          showApprovalForm && (
            <ProductRequestApprovalForm
              request={request}
              onApprove={handleApprove}
              onCancel={() => setShowApprovalForm(false)}
              isSubmitting={false}
            />
          )}
      </div>

      <div className="sticky bottom-0 flex items-center justify-end gap-3 border-t border-borderDefault bg-bgWhite px-8 py-4 max-md:flex-wrap max-md:px-4 max-md:py-3">
        <Button variant="secondary" size="md" onClick={() => navigate(backRoute)}>
          Back
        </Button>
      </div>
    </div>
  );
}
